import { http } from '@/api/http';
import { isAdminLocalModeEnabled, isPublicMockEnabled } from '@/config/featureFlags';
import {
  createMockConstructionCase as mockCreateConstructionCase,
  createMockPopup as mockCreatePopup,
  createMockProjectRecord as mockCreateProjectRecord,
  createMockSupportMaterial as mockCreateSupportMaterial,
  deleteMockConstructionCase as mockDeleteConstructionCase,
  deleteMockPopup as mockDeletePopup,
  deleteMockProjectRecord as mockDeleteProjectRecord,
  deleteMockSupportMaterial as mockDeleteSupportMaterial,
  fetchMockAdminConstructionCase as mockFetchAdminConstructionCase,
  fetchMockAdminConstructionCases as mockFetchAdminConstructionCases,
  fetchMockAdminInquiry as mockFetchAdminInquiry,
  fetchMockAdminInquiries as mockFetchAdminInquiries,
  fetchMockAdminPopup as mockFetchAdminPopup,
  fetchMockAdminPopups as mockFetchAdminPopups,
  fetchMockAdminProjectRecord as mockFetchAdminProjectRecord,
  fetchMockAdminProjectRecords as mockFetchAdminProjectRecords,
  fetchMockAdminSupportMaterial as mockFetchAdminSupportMaterial,
  fetchMockAdminSupportMaterials as mockFetchAdminSupportMaterials,
  fetchMockConstructionCase as mockFetchConstructionCase,
  fetchMockConstructionCases as mockFetchConstructionCases,
  fetchMockProjectRecords as mockFetchProjectRecords,
  fetchMockSupportMaterial as mockFetchSupportMaterial,
  fetchMockSupportMaterials as mockFetchSupportMaterials,
  loginMockAdmin,
  logoutMockAdminSession,
  processMockInquiry as mockProcessInquiry,
  refreshMockAdminSession,
  updateMockConstructionCase as mockUpdateConstructionCase,
  updateMockConstructionCaseFeatured as mockUpdateConstructionCaseFeatured,
  updateMockPopup as mockUpdatePopup,
  updateMockProjectRecord as mockUpdateProjectRecord,
  updateMockSupportMaterial as mockUpdateSupportMaterial,
} from '@/features/board/mockData';
import type { ProjectRecordCategory } from '@/features/board/projectRecordCategory';
import { withBoardReadFallback } from '@/features/board/requestFallback';

export interface PopupSummary {
  id: number;
  title: string;
  summary: string;
  linkUrl: string;
  imageUrl: string;
  imageFit: 'contain' | 'cover';
  imageObjectPosition: string;
  imageScale: number;
  isPinned: boolean;
  publishedAt: string;
  translations?: PopupTranslation[];
}

export interface PopupDetail extends PopupSummary {
  content: string;
  imageFileName: string;
  imageContentType: string;
  imageFileSize: number;
  isPublished: boolean;
}

export interface AdminPopupSummary extends PopupSummary {
  isPublished: boolean;
}

export interface PopupTranslation {
  locale: 'en' | 'ko';
  status: BoardTranslationStatus;
  title: string;
}

export interface PopupMutationInput {
  title: string;
  linkUrl: string;
  image: File | null;
  imageFit: 'contain' | 'cover';
  imageObjectPosition: string;
  imageScale: number;
  isPublished: boolean;
  publishedAt: string;
  translations?: PopupTranslation[];
}

export interface ConstructionCaseGalleryImage {
  id: number;
  displayOrder: number;
  fileName: string;
  fileUrl: string;
  contentType: string;
  fileSize: number;
  imageFit: 'contain' | 'cover';
  imageObjectPosition: string;
  imageScale: number;
}

export interface ConstructionCaseFeaturePoint {
  title: string;
  description: string;
}

export type BoardTranslationStatus = 'DRAFT' | 'READY';

export interface ConstructionCaseTranslation {
  category: string;
  clientName: string;
  content: string;
  featurePoints: ConstructionCaseFeaturePoint[];
  locale: 'en' | 'ko';
  location: string;
  overview: string;
  scopeItems: string[];
  status: BoardTranslationStatus;
  summary: string;
  title: string;
}

export interface ConstructionCaseImagePlacement {
  imageFit: 'contain' | 'cover';
  imageObjectPosition: string;
  imageScale: number;
}

export interface ExistingConstructionCaseGalleryPlacement extends ConstructionCaseImagePlacement {
  id: number;
}

export interface ConstructionCaseSummary {
  id: number;
  slug: string;
  title: string;
  summary: string;
  category?: string;
  clientName?: string;
  thumbnailFileUrl: string;
  thumbnailFit: 'contain' | 'cover';
  thumbnailObjectPosition: string;
  thumbnailScale: number;
  isFeatured: boolean;
  publishedAt: string;
  translations?: ConstructionCaseTranslation[];
}

export interface ConstructionCaseDetail extends ConstructionCaseSummary {
  thumbnailFileName: string;
  thumbnailContentType: string;
  thumbnailFileSize: number;
  content: string;
  viewCount: number;
  isPublished: boolean;
  galleryImages: ConstructionCaseGalleryImage[];
  constructionStartDate?: string;
  constructionEndDate?: string;
  location?: string;
  overview?: string;
  scopeItems?: string[];
  featurePoints?: ConstructionCaseFeaturePoint[];
  translations?: ConstructionCaseTranslation[];
}

export interface AdminConstructionCaseSummary extends ConstructionCaseSummary {
  viewCount: number;
  isFeatured: boolean;
  isPublished: boolean;
}

export interface ProjectRecordSummary {
  id: number;
  title: string;
  summary: string;
  viewCount: number;
  publishedAt: string;
  category?: string;
  clientName?: string;
  constructionStartDate?: string;
  constructionEndDate?: string;
  thumbnailFileUrl?: string;
  translations?: ProjectRecordTranslation[];
}

export interface ProjectRecordDetail extends ProjectRecordSummary {
  content: string;
  isPublished: boolean;
  location?: string;
  overview?: string;
  scopeItems?: string[];
  featurePoints?: Array<{
    title: string;
    description: string;
  }>;
  thumbnailFileUrl?: string;
  galleryImages?: ConstructionCaseGalleryImage[];
}

export interface AdminProjectRecordSummary extends ProjectRecordSummary {
  isPublished: boolean;
}

export interface ProjectRecordTranslation {
  category: string;
  clientName: string;
  content: string;
  locale: 'en' | 'ko';
  status: BoardTranslationStatus;
  summary: string;
  title: string;
}

export interface SupportMaterialSummary {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  categoryCode: string;
  assetKind: SupportMaterialAssetKind;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  videoUrl: string;
  videoProvider: string;
  viewCount: number;
  publishedAt: string;
  translations?: SupportMaterialTranslation[];
}

export type SupportMaterialAssetKind = 'FILE' | 'VIDEO' | 'IMAGE';

export interface SupportMaterialTranslation {
  categoryName: string;
  content: string;
  description: string;
  locale: 'en' | 'ko';
  status: BoardTranslationStatus;
  title: string;
}

export interface SupportMaterialImage {
  id: number;
  displayOrder: number;
  fileName: string;
  fileUrl: string;
  contentType: string;
  fileSize: number;
}

export interface SupportMaterialDetail extends SupportMaterialSummary {
  content: string;
  contentType: string;
  images: SupportMaterialImage[];
  isPublished: boolean;
}

export interface AdminSupportMaterialSummary extends SupportMaterialSummary {
  isPublished: boolean;
}

export interface AdminInquirySummary {
  id: number;
  categoryCode: number;
  categoryName: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  status: string;
  createdAt: string;
}

export interface InquiryCategory {
  code: number;
  name: string;
}

export interface InquiryCreateInput {
  categoryCode: number;
  companyName: string;
  contactName: string;
  department?: string;
  phone: string;
  email: string;
  message: string;
  agreePrivacy: boolean;
  agreeMarketing: boolean;
}

export interface InquirySubmitResponse {
  id: number;
  status: string;
  createdAt: string;
}

export interface AdminInquiryDetail extends AdminInquirySummary {
  department: string;
  message: string;
  agreePrivacy: boolean;
  agreeMarketing: boolean;
  adminMemo: string;
  assignedAdminUsername: string;
  respondedAt: string | null;
  updatedAt: string;
}

export interface AdminInquiryProcessInput {
  status: string;
  adminMemo: string;
  assignedAdminUsername: string;
}

export interface AdminLoginResponse {
  accessToken: string;
  tokenType: string;
  expiresAt: string;
}

export interface ConstructionCaseMutationInput {
  title: string;
  summary: string;
  content: string;
  category: string;
  clientName: string;
  constructionStartDate: string;
  constructionEndDate: string;
  location: string;
  overview: string;
  scopeItems: string[];
  featurePoints: ConstructionCaseFeaturePoint[];
  isFeatured: boolean;
  isPublished: boolean;
  publishedAt: string;
  thumbnail: File | null;
  thumbnailFit: 'contain' | 'cover';
  thumbnailObjectPosition: string;
  thumbnailScale: number;
  galleryImages: File[];
  galleryImageSlots: string[];
  galleryImagePlacements: ConstructionCaseImagePlacement[];
  existingGalleryImagePlacements: ExistingConstructionCaseGalleryPlacement[];
  removeGallery: boolean;
  translations?: ConstructionCaseTranslation[];
}

export interface ProjectRecordMutationInput {
  title: string;
  summary: string;
  category: ProjectRecordCategory;
  clientName: string;
  constructionStartDate: string;
  constructionEndDate: string;
  content: string;
  isPublished: boolean;
  publishedAt: string;
  translations?: ProjectRecordTranslation[];
}

export interface SupportMaterialMutationInput {
  title: string;
  description: string;
  content: string;
  category: string;
  assetKind: SupportMaterialAssetKind;
  videoUrl: string;
  isPublished: boolean;
  publishedAt: string;
  file: File | null;
  imageFiles: File[];
  translations?: SupportMaterialTranslation[];
}

interface BackendSupportMaterialSummary {
  id: number;
  slug: string;
  title: string;
  description: string;
  categoryCode: string;
  categoryName: string;
  assetKind: SupportMaterialAssetKind;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  videoUrl: string;
  videoProvider: string;
  viewCount: number;
  publishedAt: string;
  translations?: SupportMaterialTranslation[];
}

interface BackendSupportMaterialDetail extends BackendSupportMaterialSummary {
  content: string;
  contentType: string;
  fileSize: number;
  videoProvider: string;
  images?: SupportMaterialImage[];
  isPublished: boolean;
}

interface BackendAdminSupportMaterialSummary {
  id: number;
  slug: string;
  title: string;
  description: string;
  categoryCode: string;
  categoryName: string;
  assetKind: SupportMaterialAssetKind;
  isPublished: boolean;
  publishedAt: string;
  translations?: SupportMaterialTranslation[];
}

const shouldUseBoardMocks = isPublicMockEnabled();
const shouldUseAdminBoardMocks = isAdminLocalModeEnabled();

const isEducationMaterialCategory = (category: string) => category === '교육자료';

const toSupportMaterialCategoryCode = (category: string) => {
  return isEducationMaterialCategory(category) ? 'EDUCATION_MATERIAL' : 'TECHNICAL_DATA';
};

const toSupportMaterialCategoryName = (categoryCode: string, fallbackName?: string) => {
  if (fallbackName) {
    return fallbackName;
  }

  return categoryCode === 'EDUCATION_MATERIAL' ? '교육자료' : '기술자료';
};

const mapSupportMaterialSummary = (
  supportMaterial: BackendSupportMaterialSummary,
): SupportMaterialSummary => {
  return {
    id: supportMaterial.id,
    slug: supportMaterial.slug,
    title: supportMaterial.title,
    description: supportMaterial.description,
    category: toSupportMaterialCategoryName(
      supportMaterial.categoryCode,
      supportMaterial.categoryName,
    ),
    categoryCode: supportMaterial.categoryCode,
    assetKind: supportMaterial.assetKind,
    fileName: supportMaterial.fileName,
    fileUrl: supportMaterial.fileUrl,
    fileSize: supportMaterial.fileSize,
    videoUrl: supportMaterial.videoUrl,
    videoProvider: supportMaterial.videoProvider,
    viewCount: supportMaterial.viewCount,
    publishedAt: supportMaterial.publishedAt,
    translations: supportMaterial.translations ?? [],
  };
};

const mapSupportMaterialDetail = (
  supportMaterial: BackendSupportMaterialDetail,
): SupportMaterialDetail => {
  return {
    ...mapSupportMaterialSummary(supportMaterial),
    content: supportMaterial.content,
    contentType: supportMaterial.contentType,
    fileSize: supportMaterial.fileSize,
    images: supportMaterial.images ?? [],
    isPublished: supportMaterial.isPublished,
    videoProvider: supportMaterial.videoProvider,
  };
};

const mapAdminSupportMaterialSummary = (
  supportMaterial: BackendAdminSupportMaterialSummary,
): AdminSupportMaterialSummary => {
  return {
    id: supportMaterial.id,
    slug: supportMaterial.slug,
    title: supportMaterial.title,
    description: supportMaterial.description,
    category: toSupportMaterialCategoryName(
      supportMaterial.categoryCode,
      supportMaterial.categoryName,
    ),
    categoryCode: supportMaterial.categoryCode,
    assetKind: supportMaterial.assetKind,
    fileName: '',
    fileUrl: '',
    fileSize: 0,
    videoUrl: '',
    videoProvider: supportMaterial.assetKind === 'VIDEO' ? 'YOUTUBE' : '',
    viewCount: 0,
    publishedAt: supportMaterial.publishedAt,
    isPublished: supportMaterial.isPublished,
    translations: supportMaterial.translations ?? [],
  };
};

export const boardQueryKeys = {
  constructionCases: ['board', 'construction-cases'] as const,
  constructionCase: (constructionCaseId: number | string) =>
    ['board', 'construction-cases', constructionCaseId] as const,
  inquiryCategories: ['board', 'inquiries', 'categories'] as const,
  projectRecords: ['board', 'project-records'] as const,
  supportMaterials: ['board', 'support-materials'] as const,
  supportMaterial: (supportMaterialId: number | string) =>
    ['board', 'support-materials', supportMaterialId] as const,
  adminPopups: ['board', 'admin', 'popups'] as const,
  adminPopup: (popupId: number) => ['board', 'admin', 'popups', popupId] as const,
  adminConstructionCases: ['board', 'admin', 'construction-cases'] as const,
  adminConstructionCase: (constructionCaseId: number) =>
    ['board', 'admin', 'construction-cases', constructionCaseId] as const,
  adminProjectRecords: ['board', 'admin', 'project-records'] as const,
  adminProjectRecord: (projectRecordId: number) =>
    ['board', 'admin', 'project-records', projectRecordId] as const,
  adminSupportMaterials: ['board', 'admin', 'support-materials'] as const,
  adminSupportMaterial: (supportMaterialId: number) =>
    ['board', 'admin', 'support-materials', supportMaterialId] as const,
  adminInquiries: ['board', 'admin', 'inquiries'] as const,
  adminInquiry: (inquiryId: number) => ['board', 'admin', 'inquiries', inquiryId] as const,
};

export const loginAdmin = async (credentials: {
  username: string;
  password: string;
}): Promise<AdminLoginResponse> => {
  if (shouldUseAdminBoardMocks) {
    return loginMockAdmin(credentials);
  }

  return http.post<AdminLoginResponse>('/api/v1/admin/auth/login', credentials);
};

export const refreshAdminSession = async (): Promise<AdminLoginResponse> => {
  if (shouldUseAdminBoardMocks) {
    return refreshMockAdminSession();
  }

  return http.post<AdminLoginResponse>('/api/v1/admin/auth/refresh');
};

export const logoutAdminSession = async (): Promise<void> => {
  if (shouldUseAdminBoardMocks) {
    return logoutMockAdminSession();
  }

  return http.post<undefined>('/api/v1/admin/auth/logout');
};

export const fetchConstructionCases = async (): Promise<ConstructionCaseSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<ConstructionCaseSummary[]>('/api/v1/construction-cases'),
    fallbackRequest: mockFetchConstructionCases,
    label: 'construction cases list',
    useMock: shouldUseBoardMocks,
  });
};

export const fetchConstructionCase = async (
  constructionCaseId: number | string,
): Promise<ConstructionCaseDetail> => {
  return withBoardReadFallback({
    apiRequest: () =>
      http.get<ConstructionCaseDetail>(`/api/v1/construction-cases/${String(constructionCaseId)}`),
    fallbackRequest: () => mockFetchConstructionCase(constructionCaseId),
    label: `construction case detail:${String(constructionCaseId)}`,
    useMock: shouldUseBoardMocks,
  });
};

export const fetchProjectRecords = async (): Promise<ProjectRecordSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<ProjectRecordSummary[]>('/api/v1/project-records'),
    fallbackRequest: mockFetchProjectRecords,
    label: 'project records list',
    useMock: shouldUseBoardMocks,
  });
};

export const fetchSupportMaterials = async (): Promise<SupportMaterialSummary[]> => {
  return withBoardReadFallback({
    apiRequest: async () => {
      const response = await http.get<BackendSupportMaterialSummary[]>('/api/v1/support-materials');
      return response.map(mapSupportMaterialSummary);
    },
    fallbackOnError: false,
    fallbackRequest: mockFetchSupportMaterials,
    label: 'technical data list',
    useMock: shouldUseBoardMocks,
  });
};

export const fetchSupportMaterial = async (
  supportMaterialId: number | string,
): Promise<SupportMaterialDetail> => {
  return withBoardReadFallback({
    apiRequest: async () => {
      const response = await http.get<BackendSupportMaterialDetail>(
        `/api/v1/support-materials/${String(supportMaterialId)}`,
      );
      return mapSupportMaterialDetail(response);
    },
    fallbackOnError: false,
    fallbackRequest: () => mockFetchSupportMaterial(supportMaterialId),
    label: `support material detail:${String(supportMaterialId)}`,
    useMock: shouldUseBoardMocks,
  });
};

export const fetchAdminPopups = async (): Promise<AdminPopupSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<AdminPopupSummary[]>('/api/v1/admin/popups'),
    fallbackRequest: mockFetchAdminPopups,
    label: 'admin popups list',
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchAdminPopup = async (popupId: number): Promise<PopupDetail> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<PopupDetail>(`/api/v1/admin/popups/${String(popupId)}`),
    fallbackRequest: () => mockFetchAdminPopup(popupId),
    label: `admin popup detail:${String(popupId)}`,
    useMock: shouldUseAdminBoardMocks,
  });
};

export const createPopup = async (input: PopupMutationInput): Promise<PopupDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockCreatePopup(input);
  }

  return http.post<PopupDetail>('/api/v1/admin/popups', toPopupFormData(input));
};

export const updatePopup = async (
  popupId: number,
  input: PopupMutationInput,
): Promise<PopupDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockUpdatePopup(popupId, input);
  }

  return http.put<PopupDetail>(`/api/v1/admin/popups/${String(popupId)}`, toPopupFormData(input));
};

export const deletePopup = async (popupId: number): Promise<void> => {
  if (shouldUseAdminBoardMocks) {
    return mockDeletePopup(popupId);
  }

  return http.delete<undefined>(`/api/v1/admin/popups/${String(popupId)}`);
};

export const fetchAdminConstructionCases = async (): Promise<AdminConstructionCaseSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<AdminConstructionCaseSummary[]>('/api/v1/admin/construction-cases'),
    fallbackRequest: mockFetchAdminConstructionCases,
    label: 'admin construction cases list',
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchAdminConstructionCase = async (
  constructionCaseId: number,
): Promise<ConstructionCaseDetail> => {
  return withBoardReadFallback({
    apiRequest: () =>
      http.get<ConstructionCaseDetail>(
        `/api/v1/admin/construction-cases/${String(constructionCaseId)}`,
      ),
    fallbackRequest: () => mockFetchAdminConstructionCase(constructionCaseId),
    label: `admin construction case detail:${String(constructionCaseId)}`,
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchAdminProjectRecords = async (): Promise<AdminProjectRecordSummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<AdminProjectRecordSummary[]>('/api/v1/admin/project-records'),
    fallbackRequest: mockFetchAdminProjectRecords,
    label: 'admin project records list',
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchAdminProjectRecord = async (
  projectRecordId: number,
): Promise<ProjectRecordDetail> => {
  return withBoardReadFallback({
    apiRequest: () =>
      http.get<ProjectRecordDetail>(`/api/v1/admin/project-records/${String(projectRecordId)}`),
    fallbackRequest: () => mockFetchAdminProjectRecord(projectRecordId),
    label: `admin project record detail:${String(projectRecordId)}`,
    useMock: shouldUseAdminBoardMocks,
  });
};

export const createProjectRecord = async (
  input: ProjectRecordMutationInput,
): Promise<ProjectRecordDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockCreateProjectRecord(input);
  }

  return http.post<ProjectRecordDetail>('/api/v1/admin/project-records', input);
};

export const updateProjectRecord = async (
  projectRecordId: number,
  input: ProjectRecordMutationInput,
): Promise<ProjectRecordDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockUpdateProjectRecord(projectRecordId, input);
  }

  return http.put<ProjectRecordDetail>(
    `/api/v1/admin/project-records/${String(projectRecordId)}`,
    input,
  );
};

export const deleteProjectRecord = async (projectRecordId: number): Promise<void> => {
  if (shouldUseAdminBoardMocks) {
    return mockDeleteProjectRecord(projectRecordId);
  }

  return http.delete<undefined>(`/api/v1/admin/project-records/${String(projectRecordId)}`);
};

export const fetchAdminSupportMaterials = async (): Promise<AdminSupportMaterialSummary[]> => {
  return withBoardReadFallback({
    apiRequest: async () => {
      const response = await http.get<BackendAdminSupportMaterialSummary[]>(
        '/api/v1/admin/support-materials',
      );
      return response.map(mapAdminSupportMaterialSummary);
    },
    fallbackRequest: mockFetchAdminSupportMaterials,
    label: 'admin technical data list',
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchAdminSupportMaterial = async (
  supportMaterialId: number,
): Promise<SupportMaterialDetail> => {
  return withBoardReadFallback({
    apiRequest: async () => {
      const response = await http.get<BackendSupportMaterialDetail>(
        `/api/v1/admin/support-materials/${String(supportMaterialId)}`,
      );
      return mapSupportMaterialDetail(response);
    },
    fallbackRequest: () => mockFetchAdminSupportMaterial(supportMaterialId),
    label: `admin support material detail:${String(supportMaterialId)}`,
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchAdminInquiries = async (): Promise<AdminInquirySummary[]> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<AdminInquirySummary[]>('/api/v1/admin/inquiries'),
    fallbackRequest: mockFetchAdminInquiries,
    label: 'admin inquiries list',
    useMock: shouldUseAdminBoardMocks,
  });
};

export const fetchInquiryCategories = async (): Promise<InquiryCategory[]> => {
  return http.get<InquiryCategory[]>('/api/v1/inquiries/categories');
};

export const createInquiry = async (input: InquiryCreateInput): Promise<InquirySubmitResponse> => {
  return http.post<InquirySubmitResponse>('/api/v1/inquiries', input);
};

export const fetchAdminInquiry = async (inquiryId: number): Promise<AdminInquiryDetail> => {
  return withBoardReadFallback({
    apiRequest: () => http.get<AdminInquiryDetail>(`/api/v1/admin/inquiries/${String(inquiryId)}`),
    fallbackRequest: () => mockFetchAdminInquiry(inquiryId),
    label: `admin inquiry detail:${String(inquiryId)}`,
    useMock: shouldUseAdminBoardMocks,
  });
};

export const processAdminInquiry = async (
  inquiryId: number,
  input: AdminInquiryProcessInput,
): Promise<AdminInquiryDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockProcessInquiry(inquiryId, input);
  }

  return http.put<AdminInquiryDetail>(`/api/v1/admin/inquiries/${String(inquiryId)}`, input);
};

export const createSupportMaterial = async (
  input: SupportMaterialMutationInput,
): Promise<SupportMaterialDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockCreateSupportMaterial(input);
  }

  return http.post<SupportMaterialDetail>(
    '/api/v1/admin/support-materials',
    toSupportMaterialFormData(input),
  );
};

export const updateSupportMaterial = async (
  supportMaterialId: number,
  input: SupportMaterialMutationInput,
): Promise<SupportMaterialDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockUpdateSupportMaterial(supportMaterialId, input);
  }

  return http.put<SupportMaterialDetail>(
    `/api/v1/admin/support-materials/${String(supportMaterialId)}`,
    toSupportMaterialFormData(input),
  );
};

export const deleteSupportMaterial = async (supportMaterialId: number): Promise<void> => {
  if (shouldUseAdminBoardMocks) {
    return mockDeleteSupportMaterial(supportMaterialId);
  }

  return http.delete<undefined>(`/api/v1/admin/support-materials/${String(supportMaterialId)}`);
};

export const createConstructionCase = async (
  input: ConstructionCaseMutationInput,
): Promise<ConstructionCaseDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockCreateConstructionCase(input);
  }

  return http.post<ConstructionCaseDetail>(
    '/api/v1/admin/construction-cases',
    toConstructionCaseFormData(input),
  );
};

export const updateConstructionCase = async (
  constructionCaseId: number,
  input: ConstructionCaseMutationInput,
): Promise<ConstructionCaseDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockUpdateConstructionCase(constructionCaseId, input);
  }

  return http.put<ConstructionCaseDetail>(
    `/api/v1/admin/construction-cases/${String(constructionCaseId)}`,
    toConstructionCaseFormData(input),
  );
};

export const deleteConstructionCase = async (constructionCaseId: number): Promise<void> => {
  if (shouldUseAdminBoardMocks) {
    return mockDeleteConstructionCase(constructionCaseId);
  }

  return http.delete<undefined>(`/api/v1/admin/construction-cases/${String(constructionCaseId)}`);
};

export const updateConstructionCaseFeatured = async (
  constructionCaseId: number,
  isFeatured: boolean,
): Promise<ConstructionCaseDetail> => {
  if (shouldUseAdminBoardMocks) {
    return mockUpdateConstructionCaseFeatured(constructionCaseId, isFeatured);
  }

  return http.patch<ConstructionCaseDetail>(
    `/api/v1/admin/construction-cases/${String(constructionCaseId)}/featured`,
    { isFeatured },
  );
};

const toConstructionCaseFormData = (input: ConstructionCaseMutationInput): FormData => {
  const toDateTimeFieldValue = (value: string) => {
    if (!value || value.includes('T')) {
      return value;
    }

    return `${value}T00:00`;
  };

  const formData = new FormData();
  formData.append('title', input.title);
  formData.append('summary', input.summary);
  formData.append('content', input.content);
  formData.append('category', input.category);
  formData.append('clientName', input.clientName);
  formData.append('constructionStartDate', toDateTimeFieldValue(input.constructionStartDate));
  formData.append('constructionEndDate', toDateTimeFieldValue(input.constructionEndDate));
  formData.append('location', input.location);
  formData.append('overview', input.overview);
  formData.append('thumbnailFit', input.thumbnailFit);
  formData.append('thumbnailObjectPosition', input.thumbnailObjectPosition);
  formData.append('thumbnailScale', String(input.thumbnailScale));
  formData.append('isFeatured', String(input.isFeatured));
  formData.append('isPublished', String(input.isPublished));
  formData.append('publishedAt', input.publishedAt);
  formData.append('removeGallery', String(input.removeGallery));

  input.scopeItems.forEach((item) => {
    formData.append('scopeItems', item);
  });

  input.featurePoints.forEach((point) => {
    formData.append('featurePointTitles', point.title);
    formData.append('featurePointDescriptions', point.description);
  });
  if (input.translations?.length) {
    formData.append('translationsJson', JSON.stringify(input.translations));
  }

  if (input.thumbnail) {
    formData.append('thumbnail', input.thumbnail);
  }

  input.galleryImages.forEach((file) => {
    formData.append('galleryImages', file);
  });
  formData.append('galleryImageSlotsJson', JSON.stringify(input.galleryImageSlots));
  input.galleryImagePlacements.forEach((placement) => {
    formData.append('galleryImageFits', placement.imageFit);
    formData.append('galleryImageObjectPositions', placement.imageObjectPosition);
    formData.append('galleryImageScales', String(placement.imageScale));
  });
  input.existingGalleryImagePlacements.forEach((placement) => {
    formData.append('existingGalleryImageIds', String(placement.id));
    formData.append('existingGalleryImageFits', placement.imageFit);
    formData.append('existingGalleryImageObjectPositions', placement.imageObjectPosition);
    formData.append('existingGalleryImageScales', String(placement.imageScale));
  });

  return formData;
};

const toPopupFormData = (input: PopupMutationInput): FormData => {
  const formData = new FormData();
  formData.append('title', input.title);
  formData.append('linkUrl', input.linkUrl.trim());
  formData.append('imageFit', input.imageFit);
  formData.append('imageObjectPosition', input.imageObjectPosition);
  formData.append('imageScale', String(input.imageScale));
  formData.append('isPublished', String(input.isPublished));
  formData.append('publishedAt', input.publishedAt);
  if (input.translations?.length) {
    formData.append('translationsJson', JSON.stringify(input.translations));
  }

  if (input.image) {
    formData.append('image', input.image);
  }

  return formData;
};

const toSupportMaterialFormData = (input: SupportMaterialMutationInput): FormData => {
  const formData = new FormData();
  formData.append('title', input.title);
  formData.append('description', input.description);
  formData.append('content', input.content);
  formData.append('categoryCode', toSupportMaterialCategoryCode(input.category));
  formData.append('assetKind', input.assetKind);
  formData.append('isPublished', String(input.isPublished));
  formData.append('publishedAt', input.publishedAt);
  if (input.translations?.length) {
    formData.append('translationsJson', JSON.stringify(input.translations));
  }

  if (input.assetKind === 'VIDEO') {
    formData.append('videoUrl', input.videoUrl.trim());
    formData.append('videoProvider', 'YOUTUBE');
  }

  if (input.assetKind === 'FILE' && input.file) {
    formData.append('file', input.file);
  }

  if (input.assetKind === 'IMAGE') {
    input.imageFiles.forEach((imageFile) => {
      formData.append('imageFiles', imageFile);
    });
  }

  return formData;
};
